/**
 * Tag Manager Dialog
 * Manages the tag vocabulary as a tree of stable tag ids with display names.
 * Hierarchy is derived from parentIds, not from the stored id string.
 * Left pane: tree. Right pane: detail panel for the selected tag.
 */

import {
  Add,
  Check,
  ChevronLeft,
  ChevronRight,
  Close,
  Delete,
  Edit,
  ExpandMore,
  Search,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { DISCUSSED_TAG_ID, DISCUSSED_TAG_NAME } from '@shared/tags/canonicalDiscussedTag'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'

import {
  TagDefinition,
  TagTreeNode,
  buildTree,
  collectHiddenIds,
  getNodeCount,
  getTagDisplayName,
} from './tagTreeUtils'
import { CHARACTERS_ROOT_TAG_ID } from '../../../../shared/tags/canonicalCharacterTags'
import { MOMENT_TAG_ID, MOMENT_TAG_NAME } from '../../../../shared/tags/canonicalMomentTag'
import { PINNED_TAG_ID, PINNED_TAG_NAME } from '../../../../shared/tags/canonicalPinnedTag'
import { collectDescendantIds, createTagId } from '../../../../shared/tags/tagRegistry'
import { apiService } from '../../services/api'
import { logger } from '../../utils/logger'

export interface TagManagerDialogProps {
  open: boolean
  onClose: () => void
  bookId: string
  direction?: 'ltr' | 'rtl'
  mode?: 'manage' | 'select'
  selectionMode?: 'single' | 'multiple'
  initialSelectedTags?: string[]
  onSelectAndSave?: (selectedTag: string) => void
  onSelectManyAndSave?: (selectedTags: string[]) => void
  onRegistrySaved?: (tags: TagMap) => void
}

type TagMap = Record<string, TagDefinition>

const PROTECTED_TAG_IDS = [CHARACTERS_ROOT_TAG_ID, MOMENT_TAG_ID, PINNED_TAG_ID, DISCUSSED_TAG_ID] as const

function isProtectedTag(tagId: string): boolean {
  return PROTECTED_TAG_IDS.includes(tagId as any)
}

function getProtectedTagName(tagId: string): string {
  switch (tagId) {
    case CHARACTERS_ROOT_TAG_ID:
      return 'דמויות'
    case MOMENT_TAG_ID:
      return MOMENT_TAG_NAME
    case DISCUSSED_TAG_ID:
      return DISCUSSED_TAG_NAME
    case PINNED_TAG_ID:
      return PINNED_TAG_NAME
    default:
      return 'protected tag'
  }
}

// ---------------------------------------------------------------------------
// Preset colors
// ---------------------------------------------------------------------------

const TAG_COLORS = [
  { value: '#E38CA7', label: 'rose' },
  { value: '#C85C8E', label: 'raspberry' },
  { value: '#D8A6E8', label: 'orchid' },
  { value: '#8E6AD8', label: 'violet' },
  { value: '#A8B6F3', label: 'periwinkle' },
  { value: '#4F7EE8', label: 'cobalt' },
  { value: '#8FC8F6', label: 'sky' },
  { value: '#2F9BDB', label: 'cerulean' },
  { value: '#8FDDE8', label: 'aqua' },
  { value: '#159A9C', label: 'teal' },
  { value: '#88D8C0', label: 'seafoam' },
  { value: '#2E8B57', label: 'emerald' },
  { value: '#B8D98B', label: 'sage' },
  { value: '#6E9F3A', label: 'olive' },
  { value: '#E8D87A', label: 'butter' },
  { value: '#C9A227', label: 'gold' },
  { value: '#F3C97D', label: 'apricot' },
  { value: '#E67E22', label: 'amber' },
  { value: '#F2B085', label: 'peach' },
  { value: '#D96C4A', label: 'terracotta' },
  { value: '#C98F7C', label: 'clay' },
  { value: '#8A5A44', label: 'cocoa' },
]

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const TagManagerDialog: React.FC<TagManagerDialogProps> = ({
  open,
  onClose,
  bookId,
  direction = 'ltr',
  mode = 'manage',
  selectionMode = 'single',
  initialSelectedTags = [],
  onSelectAndSave,
  onSelectManyAndSave,
  onRegistrySaved,
}) => {
  const [tags, setTags] = useState<TagMap>({})
  const [version, setVersion] = useState(1)
  const [loading, setLoading] = useState(false)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [saving, setSaving] = useState(false)
  const [usageByChapter, setUsageByChapter] = useState<Record<string, Record<string, number>>>({})

  // Selection
  const [selectedPaths, setSelectedPaths] = useState<string[]>([])
  const [lastSelectedPath, setLastSelectedPath] = useState<string | null>(null)
  const [inspectedPath, setInspectedPath] = useState<string | null>(null)

  // Inline editing state
  const [editingPath, setEditingPath] = useState<string | null>(null)
  const [editValue, setEditValue] = useState('')
  const [addingUnder, setAddingUnder] = useState<string | null>(null)
  const [addingAtRoot, setAddingAtRoot] = useState(false)
  const [newTagValue, setNewTagValue] = useState('')
  const [showHidden, setShowHidden] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [draggedTagIds, setDraggedTagIds] = useState<string[]>([])
  const [dropTarget, setDropTarget] = useState<{ id: string; position: 'before' | 'after' | 'inside' } | null>(null)
  const isSelectMode = mode === 'select'
  const isMultipleSelectMode = isSelectMode && selectionMode === 'multiple'

  // Scroll tracking to detect stuck sticky elements
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [stuckNodes, setStuckNodes] = useState<Set<string>>(new Set())
  const [expandedNodeIds, setExpandedNodeIds] = useState<Set<string>>(new Set())
  const labelToggleTimeoutRef = useRef<number | null>(null)

  // Derive flat totals from per-chapter usage data
  const usageCounts = useMemo(() => {
    const flat: Record<string, number> = {}
    for (const [tag, chapterCounts] of Object.entries(usageByChapter)) {
      let sum = 0
      for (const count of Object.values(chapterCounts)) {
        sum += count
      }
      flat[tag] = sum
    }
    return flat
  }, [usageByChapter])

  // Fetch on open
  useEffect(() => {
    if (!open) {
      return
    }
    const fetchTags = async () => {
      setLoading(true)
      try {
        const [tagsRes, usageRes] = await Promise.all([
          apiService.getTagRegistry(bookId),
          apiService.getTagUsageCounts(bookId),
        ])
        setTags(tagsRes.data.tags as TagMap)
        setVersion(tagsRes.data.version)
        setUsageByChapter(usageRes.data)
        setHasLoaded(true)
      } catch (err) {
        logger.error('Failed to load tag registry', err)
        toast.error('Failed to load tags')
      } finally {
        setLoading(false)
      }
    }
    fetchTags()
  }, [open, bookId])

  // Initialize selection when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedPaths(initialSelectedTags)
      if (initialSelectedTags.length > 0) {
        setLastSelectedPath(initialSelectedTags[0])
      }
      setInspectedPath(initialSelectedTags.length === 1 ? initialSelectedTags[0] : null)
    }
  }, [open])

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setEditingPath(null)
      setAddingUnder(null)
      setAddingAtRoot(false)
      setSelectedPaths([])
      setLastSelectedPath(null)
      setInspectedPath(null)
      setSearchQuery('')
      setShowHidden(false)
      setHasLoaded(false)
      setExpandedNodeIds(new Set())
    }
  }, [open])

  // Clear selection if the selected tag was deleted
  useEffect(() => {
    if (hasLoaded) {
      setSelectedPaths((prev) => prev.filter((tag) => tags[tag] != null))
      setInspectedPath((prev) => (prev != null && tags[prev] == null ? null : prev))
    }
  }, [tags, hasLoaded])

  // --- Operations ---

  const handleRename = (tagId: string, nextName: string) => {
    const trimmed = nextName.trim()
    const currentName = tags[tagId]?.name || ''
    if (!trimmed || trimmed === currentName) {
      setEditingPath(null)
      return
    }
    if (trimmed.includes(',')) {
      toast.error('Commas are not allowed in tag names')
      return
    }

    const existing = Object.entries(tags).find(([id, tag]) => id !== tagId && tag.name === trimmed)
    if (existing) {
      toast.error('Tag name already exists')
      return
    }

    setTags((prev) => ({
      ...prev,
      [tagId]: { ...prev[tagId], name: trimmed },
    }))
    setEditingPath(null)
  }

  const handleDelete = (tagId: string) => {
    if (isProtectedTag(tagId)) {
      toast.error(`The ${getProtectedTagName(tagId)} tag cannot be removed`)
      return
    }
    const idsToDelete = collectDescendantIds(tags, tagId)
    const descendantCount = Math.max(0, idsToDelete.size - 1)
    if (descendantCount > 0) {
      if (
        !window.confirm(
          `Delete "${tags[tagId]?.name || tagId}" and ${descendantCount} descendant${descendantCount !== 1 ? 's' : ''}?`,
        )
      ) {
        return
      }
    }
    const updated: TagMap = {}
    for (const [k, v] of Object.entries(tags)) {
      if (!idsToDelete.has(k)) {
        updated[k] = v
      }
    }
    setTags(updated)
  }

  const handleAdd = (parentPath: string | null) => {
    const candidates = newTagValue
      .split(',')
      .map((segment) => segment.trim())
      .filter((segment) => segment.length > 0)

    if (candidates.length === 0) {
      setAddingUnder(null)
      setAddingAtRoot(false)
      return
    }

    const seen = new Set<string>()
    const toCreate: string[] = []
    const duplicates: string[] = []

    for (const segment of candidates) {
      const existing = Object.values(tags).some((tag) => tag.name === segment)
      if (existing || seen.has(segment)) {
        duplicates.push(segment)
        continue
      }
      seen.add(segment)
      toCreate.push(segment)
    }

    if (toCreate.length === 0) {
      toast.error('All tags already exist')
      return
    }

    const nextTags: TagMap = { ...tags }
    const createdIds: string[] = []
    for (const name of toCreate) {
      let id = createTagId(name)
      while (nextTags[id] != null) {
        id = createTagId(name)
      }
      nextTags[id] = { name, parentIds: parentPath ? [parentPath] : [] }
      createdIds.push(id)
    }
    setTags(nextTags)
    if (duplicates.length > 0) {
      toast.error(`${duplicates.length} tag${duplicates.length !== 1 ? 's were' : ' was'} skipped (already exist)`)
    }
    setNewTagValue('')
    setAddingUnder(null)
    setAddingAtRoot(false)
    setSelectedPaths([createdIds[createdIds.length - 1]])
    setLastSelectedPath(createdIds[createdIds.length - 1])
  }

  // --- Detail panel handlers ---

  const updateMeta = (path: string, patch: Partial<TagDefinition>) => {
    setTags((prev) => ({
      ...prev,
      [path]: { ...prev[path], ...patch },
    }))
  }

  const handleToggleHidden = (tagId: string) => {
    updateMeta(tagId, { hidden: !tags[tagId]?.hidden })
  }

  const reorderSiblings = (activeIds: string[], overId: string, position: 'before' | 'after') => {
    const parentId = tags[overId]?.parentIds?.[0] || null

    // Filter out invalid activeIds (e.g. trying to move a node inside its own descendant)
    const validActiveIds = activeIds.filter((id) => {
      if (id === overId || id === parentId) {
        return false
      }
      const descendants = collectDescendantIds(tags, id)
      if (parentId && descendants.has(parentId)) {
        return false
      }
      return true
    })

    if (validActiveIds.length === 0) {
      return
    }

    const siblingIds = Object.entries(tags)
      .filter(([id, tag]) => !validActiveIds.includes(id) && (tag.parentIds?.[0] || null) === parentId)
      .sort((a, b) => {
        const orderDiff = (a[1].order || 0) - (b[1].order || 0)
        return orderDiff !== 0 ? orderDiff : a[1].name.localeCompare(b[1].name, 'he')
      })
      .map(([id]) => id)

    const rawToIndex = siblingIds.indexOf(overId)
    if (rawToIndex === -1) {
      return
    }

    const toIndex = rawToIndex + (position === 'after' ? 1 : 0)

    siblingIds.splice(toIndex, 0, ...validActiveIds)

    setTags((prev) => {
      const next = { ...prev }
      siblingIds.forEach((id, idx) => {
        next[id] = { ...next[id], parentIds: parentId ? [parentId] : [], order: (idx + 1) * 10 }
      })
      return next
    })
  }

  const moveTagsToParent = (activeIds: string[], parentId: string) => {
    setTags((prev) => {
      const next = { ...prev }
      const siblingOrders = Object.entries(next)
        .filter(([id, tag]) => !activeIds.includes(id) && (tag.parentIds?.[0] || null) === parentId)
        .map(([, tag]) => tag.order || 0)
      let nextOrder = siblingOrders.length > 0 ? Math.max(...siblingOrders) : 0

      activeIds.forEach((activeId) => {
        if (activeId === parentId) {
          return
        }
        const descendants = collectDescendantIds(next, activeId)
        if (descendants.has(parentId)) {
          return
        }

        nextOrder += 10
        next[activeId] = {
          ...next[activeId],
          parentIds: [parentId],
          order: nextOrder,
        }
      })
      return next
    })
  }

  const hiddenIds = useMemo(() => collectHiddenIds(tags), [tags])

  const visibleTags = useMemo(() => {
    if (showHidden) {
      return tags
    }
    return Object.fromEntries(Object.entries(tags).filter(([id]) => !hiddenIds.has(id)))
  }, [tags, hiddenIds, showHidden])

  const filterTree = (nodes: TagTreeNode[]): TagTreeNode[] => {
    const query = searchQuery.trim().toLowerCase()
    if (!query) {
      return nodes
    }
    return nodes
      .map((node) => {
        const filteredChildren = filterTree(node.children)
        const matches = node.name.toLowerCase().includes(query)
        if (matches || filteredChildren.length > 0) {
          return { ...node, children: filteredChildren }
        }
        return null
      })
      .filter((node): node is TagTreeNode => Boolean(node))
  }

  const tree = useMemo(() => filterTree(buildTree(visibleTags)), [visibleTags, searchQuery])
  const isSearchActive = searchQuery.trim().length > 0

  const collectBranchNodeIds = (node: TagTreeNode): string[] => {
    const ids = [node.id]
    for (const child of node.children) {
      if (child.children.length > 0) {
        ids.push(...collectBranchNodeIds(child))
      }
    }
    return ids
  }

  const setBranchExpanded = (node: TagTreeNode, expanded: boolean) => {
    const branchIds = collectBranchNodeIds(node)
    setExpandedNodeIds((prev) => {
      const next = new Set(prev)
      branchIds.forEach((id) => {
        if (expanded) {
          next.add(id)
        } else {
          next.delete(id)
        }
      })
      return next
    })
  }

  const toggleNodeExpanded = (node: TagTreeNode) => {
    setExpandedNodeIds((prev) => {
      const next = new Set(prev)
      if (next.has(node.id)) {
        collectBranchNodeIds(node).forEach((id) => next.delete(id))
      } else {
        next.add(node.id)
      }
      return next
    })
  }

  const clearLabelToggleTimeout = () => {
    if (labelToggleTimeoutRef.current != null) {
      window.clearTimeout(labelToggleTimeoutRef.current)
      labelToggleTimeoutRef.current = null
    }
  }

  useEffect(() => () => clearLabelToggleTimeout(), [])

  const flatVisibleIds = useMemo(() => {
    const ids: string[] = []
    const traverse = (nodes: TagTreeNode[]) => {
      for (const node of nodes) {
        ids.push(node.id)
        if (node.children.length > 0 && (isSearchActive || expandedNodeIds.has(node.id))) {
          traverse(node.children)
        }
      }
    }
    traverse(tree)
    return ids
  }, [tree, expandedNodeIds, isSearchActive])

  const visibleNodeMap = useMemo(() => {
    const entries: Array<[string, TagTreeNode]> = []
    const visit = (nodes: TagTreeNode[]) => {
      for (const node of nodes) {
        entries.push([node.id, node])
        visit(node.children)
      }
    }
    visit(tree)
    return new Map(entries)
  }, [tree])

  // IntersectionObserver for sticky headers
  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) {
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        setStuckNodes((prev) => {
          const next = new Set(prev)
          let changed = false
          entries.forEach((entry) => {
            const id = entry.target.getAttribute('data-sentinel')
            if (!id) {
              return
            }

            const rootTop = entry.rootBounds?.top ?? container.getBoundingClientRect().top
            // If it's not intersecting and it's above the root top, it's stuck
            if (!entry.isIntersecting && entry.boundingClientRect.top < rootTop) {
              if (!next.has(id)) {
                next.add(id)
                changed = true
              }
            } else {
              if (next.has(id)) {
                next.delete(id)
                changed = true
              }
            }
          })
          return changed ? next : prev
        })
      },
      {
        root: container,
        threshold: 0,
        rootMargin: '0px',
      },
    )

    const sentinels = container.querySelectorAll('[data-sentinel]')
    sentinels.forEach((el) => observer.observe(el))

    return () => observer.disconnect()
  }, [tree])

  // --- Save ---

  const handleSave = async (closeOnSuccess = true) => {
    if (isSelectMode) {
      if (isMultipleSelectMode) {
        if (selectedPaths.length === 0) {
          toast.error('Select at least one tag to continue')
          return
        }
      } else if (selectedPaths.length === 0 || tags[selectedPaths[0]] == null) {
        toast.error('Select a tag to continue')
        return
      }
    }

    setSaving(true)
    try {
      const res = await apiService.updateTagRegistry(bookId, { version, tags })
      setVersion(res.data.version)
      setTags(res.data.tags as TagMap)
      onRegistrySaved?.(res.data.tags as TagMap)

      if (isSelectMode) {
        if (isMultipleSelectMode && onSelectManyAndSave) {
          onSelectManyAndSave(selectedPaths)
        } else if (selectedPaths.length > 0 && onSelectAndSave) {
          onSelectAndSave(selectedPaths[0])
        }
      }

      if (closeOnSuccess) {
        onClose()
      } else {
        toast.success('Changes applied')
      }
    } catch (err) {
      logger.error('Failed to save tag registry', err)
      toast.error('Failed to save tags')
    } finally {
      setSaving(false)
    }
  }

  // --- Render ---

  const selectedPath = selectedPaths.length === 1 ? selectedPaths[0] : null
  const detailPath = mode === 'manage' ? (inspectedPath ?? selectedPath) : selectedPath
  const detailMeta = detailPath ? tags[detailPath] : null
  const selectedTagLabels = useMemo(
    () => selectedPaths.map((id) => ({ id, name: getTagDisplayName(tags, id) })),
    [selectedPaths, tags],
  )

  const iconBtnSx = {
    opacity: 0,
    transition: 'opacity 0.15s',
    p: 0.2,
  }

  const focusTree = () => {
    scrollContainerRef.current?.focus()
  }

  useEffect(() => {
    if (!detailPath) {
      return
    }
    const row = scrollContainerRef.current?.querySelector(`[data-tag-row="${detailPath}"]`)
    if (row instanceof HTMLElement) {
      row.scrollIntoView({ block: 'nearest' })
    }
  }, [detailPath])

  useEffect(() => {
    if (!open || loading || tree.length === 0) {
      return
    }
    const timeoutId = window.setTimeout(() => {
      focusTree()
    }, 0)
    return () => window.clearTimeout(timeoutId)
  }, [open, loading, tree.length])

  const handleTreeKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (flatVisibleIds.length === 0) {
      return
    }

    const target = event.target as HTMLElement
    const tagName = target.tagName.toLowerCase()
    if (tagName === 'input' || tagName === 'textarea' || target.isContentEditable) {
      return
    }

    const activeId = mode === 'manage' ? (inspectedPath ?? selectedPath ?? null) : (selectedPath ?? null)
    const currentIndex = activeId != null ? flatVisibleIds.indexOf(activeId) : -1
    const expandKey = direction === 'rtl' ? 'ArrowLeft' : 'ArrowRight'
    const collapseKey = direction === 'rtl' ? 'ArrowRight' : 'ArrowLeft'

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      const nextIndex =
        currentIndex === -1
          ? event.key === 'ArrowDown'
            ? 0
            : flatVisibleIds.length - 1
          : event.key === 'ArrowDown'
            ? Math.min(flatVisibleIds.length - 1, currentIndex + 1)
            : Math.max(0, currentIndex - 1)
      const nextId = flatVisibleIds[nextIndex]
      if (mode === 'manage') {
        setInspectedPath(nextId)
      } else {
        setSelectedPaths([nextId])
        setLastSelectedPath(nextId)
      }
      return
    }

    if (activeId == null) {
      return
    }

    if (event.key === expandKey) {
      const activeNode = visibleNodeMap.get(activeId)
      if (!activeNode || activeNode.children.length === 0) {
        return
      }
      event.preventDefault()
      setExpandedNodeIds((prev) => {
        if (prev.has(activeId)) {
          return prev
        }
        const next = new Set(prev)
        next.add(activeId)
        return next
      })
      return
    }

    if (event.key === collapseKey) {
      const activeNode = visibleNodeMap.get(activeId)
      const parentId = tags[activeId]?.parentIds?.[0] ?? null
      const collapseTargetId =
        activeNode && activeNode.children.length > 0 && expandedNodeIds.has(activeId) ? activeId : parentId

      if (!collapseTargetId) {
        return
      }

      event.preventDefault()
      setExpandedNodeIds((prev) => {
        if (!prev.has(collapseTargetId)) {
          return prev
        }
        const next = new Set(prev)
        const collapseTargetNode = visibleNodeMap.get(collapseTargetId)
        if (!collapseTargetNode) {
          next.delete(collapseTargetId)
          return next
        }
        collectBranchNodeIds(collapseTargetNode).forEach((id) => next.delete(id))
        return next
      })
      if (collapseTargetId !== activeId && mode === 'manage') {
        setInspectedPath(collapseTargetId)
      } else if (collapseTargetId !== activeId) {
        setSelectedPaths([collapseTargetId])
        setLastSelectedPath(collapseTargetId)
      }
    }
  }

  const handleNodeSelect = (nodeId: string, event: React.MouseEvent | React.ChangeEvent) => {
    event.stopPropagation()
    const isShift = (event as any).nativeEvent?.shiftKey || false
    const isCtrl = (event as any).nativeEvent?.metaKey || (event as any).nativeEvent?.ctrlKey || false

    if (isMultipleSelectMode) {
      if (isShift && lastSelectedPath) {
        const startIdx = flatVisibleIds.indexOf(lastSelectedPath)
        const endIdx = flatVisibleIds.indexOf(nodeId)
        if (startIdx !== -1 && endIdx !== -1) {
          const min = Math.min(startIdx, endIdx)
          const max = Math.max(startIdx, endIdx)
          const range = flatVisibleIds.slice(min, max + 1)
          if (isCtrl) {
            setSelectedPaths((prev) => Array.from(new Set([...prev, ...range])))
          } else {
            setSelectedPaths(range)
          }
          setLastSelectedPath(nodeId)
          return
        }
      }

      setSelectedPaths((prev) => (prev.includes(nodeId) ? prev.filter((id) => id !== nodeId) : [...prev, nodeId]))
      setLastSelectedPath(nodeId)
    } else if (mode === 'manage') {
      if (isShift && lastSelectedPath) {
        const startIdx = flatVisibleIds.indexOf(lastSelectedPath)
        const endIdx = flatVisibleIds.indexOf(nodeId)
        if (startIdx !== -1 && endIdx !== -1) {
          const min = Math.min(startIdx, endIdx)
          const max = Math.max(startIdx, endIdx)
          const range = flatVisibleIds.slice(min, max + 1)
          if (isCtrl) {
            setSelectedPaths((prev) => Array.from(new Set([...prev, ...range])))
          } else {
            setSelectedPaths(range)
          }
          return
        }
      }

      if (isCtrl) {
        setSelectedPaths((prev) => (prev.includes(nodeId) ? prev.filter((id) => id !== nodeId) : [...prev, nodeId]))
      } else {
        setSelectedPaths((prev) => (prev.includes(nodeId) ? [] : [nodeId]))
      }
      setLastSelectedPath(nodeId)
    } else {
      // Single select mode
      setSelectedPaths(selectedPaths.includes(nodeId) ? [] : [nodeId])
    }
  }

  const renderAddRow = (parentPath: string | null, depth: number) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.35,
        paddingInlineStart: depth * 20 + 'px',
        paddingInlineEnd: 1,
        py: 0.25,
      }}
    >
      <TextField
        size="small"
        placeholder="New tag name (comma-separated)"
        value={newTagValue}
        onChange={(e) => setNewTagValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            handleAdd(parentPath)
          }
          if (e.key === 'Escape') {
            setAddingUnder(null)
            setAddingAtRoot(false)
            setNewTagValue('')
          }
        }}
        autoFocus
        sx={{ flex: 1 }}
      />
      <IconButton size="small" onClick={() => handleAdd(parentPath)} sx={{ p: 0.35 }}>
        <Check sx={{ fontSize: 16 }} />
      </IconButton>
      <IconButton
        size="small"
        sx={{ p: 0.35 }}
        onClick={() => {
          setAddingUnder(null)
          setAddingAtRoot(false)
          setNewTagValue('')
        }}
      >
        <Close sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  )

  const renderNode = (node: TagTreeNode, depth: number) => {
    const hasChildren = node.children.length > 0
    const isExpanded = hasChildren && (isSearchActive || expandedNodeIds.has(node.id))
    const shouldStick = hasChildren && isExpanded
    const isSelected = selectedPaths.includes(node.id)
    const isActive = detailPath === node.id
    const nodeColor = node.isTag ? tags[node.id]?.color : undefined
    const count = getNodeCount(node.id, usageCounts, tags)
    const isHidden = Boolean(tags[node.id]?.hidden)
    const isDropBefore = dropTarget?.id === node.id && dropTarget.position === 'before'
    const isDropAfter = dropTarget?.id === node.id && dropTarget.position === 'after'
    const isDropInside = dropTarget?.id === node.id && dropTarget.position === 'inside'
    const collapsedIcon =
      direction === 'rtl' ? <ChevronLeft sx={{ fontSize: 18 }} /> : <ChevronRight sx={{ fontSize: 18 }} />

    return (
      <Box key={node.id} sx={{ position: 'relative' }}>
        {shouldStick && (
          <Box
            data-sentinel={node.id}
            sx={{
              position: 'absolute',
              top: -(depth * 32 + 1),
              height: '1px',
              width: '1px',
              visibility: 'hidden',
              pointerEvents: 'none',
            }}
          />
        )}
        <Box
          data-tag-row={node.id}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            paddingInlineStart: depth * 20 + 'px',
            paddingInlineEnd: 1,
            py: 0.1,
            height: '32px',
            cursor: node.isTag ? 'pointer' : 'default',
            position: shouldStick ? 'sticky' : 'relative',
            top: shouldStick ? depth * 32 : undefined,
            zIndex: shouldStick ? 20 - depth : 1,
            opacity: draggedTagIds.includes(node.id) ? 0.45 : 1,
            isolation: 'isolate',
            bgcolor: shouldStick ? (stuckNodes.has(node.id) ? 'var(--bg-surface)' : 'action.hover') : 'transparent',
            boxShadow:
              shouldStick && stuckNodes.has(node.id)
                ? '0 4px 6px -2px rgba(0,0,0,0.12), 0 2px 4px -2px rgba(0,0,0,0.08)'
                : 'none',
            transition: 'box-shadow 0.2s, background-color 0.2s',
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              bottom: shouldStick ? -1 : 0, // Cover the 1px gap between rows
              bgcolor: 'background.paper',
              zIndex: -2,
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              inset: 0,
              borderRadius: 1,
              zIndex: -1,
              bgcolor: isSelected ? 'action.selected' : isActive ? 'action.hover' : 'transparent',
              transition: 'background-color 0.15s',
            },
            '&:hover': {
              '&::after': {
                bgcolor: isSelected ? 'action.selected' : 'action.hover',
              },
              '& .node-actions': { opacity: 1 },
            },
          }}
          onDragOver={(e) => {
            if (draggedTagIds.length === 0 || draggedTagIds.includes(node.id)) {
              return
            }
            // Cannot drop inside a descendant of any dragged tag
            for (const draggedId of draggedTagIds) {
              const descendants = collectDescendantIds(tags, draggedId)
              if (descendants.has(node.id)) {
                setDropTarget((current) => (current?.id === node.id ? null : current))
                return
              }
            }
            e.preventDefault()
            const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect()
            const y = e.clientY - rect.top
            let position: 'before' | 'after' | 'inside' = 'inside'
            if (y < rect.height * 0.3) {
              position = 'before'
            } else if (y > rect.height * 0.7) {
              position = 'after'
            }

            setDropTarget({ id: node.id, position })
          }}
          onDragLeave={(e) => {
            const related = e.relatedTarget as Node | null
            if (!related || !(e.currentTarget as HTMLDivElement).contains(related)) {
              setDropTarget((current) => (current?.id === node.id ? null : current))
            }
          }}
          onDrop={(e) => {
            e.preventDefault()
            if (
              draggedTagIds.length === 0 ||
              draggedTagIds.includes(node.id) ||
              !dropTarget ||
              dropTarget.id !== node.id
            ) {
              return
            }
            if (dropTarget.position === 'inside') {
              moveTagsToParent(draggedTagIds, node.id)
            } else {
              reorderSiblings(draggedTagIds, node.id, dropTarget.position)
            }
            setDraggedTagIds([])
            setDropTarget(null)
            setSelectedPaths([])
            setLastSelectedPath(null)
          }}
        >
          {isDropBefore && (
            <Box
              sx={{
                position: 'absolute',
                top: -1,
                left: 6,
                right: 6,
                height: 2,
                borderRadius: 999,
                bgcolor: 'primary.main',
                pointerEvents: 'none',
              }}
            />
          )}
          {isDropAfter && (
            <Box
              sx={{
                position: 'absolute',
                bottom: -1,
                left: 6,
                right: 6,
                height: 2,
                borderRadius: 999,
                bgcolor: 'primary.main',
                pointerEvents: 'none',
              }}
            />
          )}
          {isDropInside && (
            <Box
              sx={{
                position: 'absolute',
                inset: 1,
                borderRadius: 1,
                outline: '2px solid',
                outlineColor: 'primary.main',
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                pointerEvents: 'none',
              }}
            />
          )}

          <Checkbox
            size="small"
            checked={isSelected}
            onClick={(e) => {
              e.stopPropagation()
              focusTree()
            }}
            onChange={(e) => handleNodeSelect(node.id, e)}
            sx={{ p: 0, mr: 0.5 }}
          />

          {hasChildren ? (
            <IconButton
              size="small"
              sx={{ p: 0.2, width: 22, height: 22, flexShrink: 0 }}
              onClick={(e) => {
                e.stopPropagation()
                focusTree()
                clearLabelToggleTimeout()
                labelToggleTimeoutRef.current = window.setTimeout(() => {
                  toggleNodeExpanded(node)
                  labelToggleTimeoutRef.current = null
                }, 200)
              }}
              onDoubleClick={(e) => {
                e.stopPropagation()
                focusTree()
                clearLabelToggleTimeout()
                setBranchExpanded(node, true)
              }}
              title={isExpanded ? 'Collapse section' : 'Expand section'}
            >
              {isExpanded ? <ExpandMore sx={{ fontSize: 18, color: 'text.secondary' }} /> : collapsedIcon}
            </IconButton>
          ) : (
            <Box sx={{ width: 22, flexShrink: 0 }} />
          )}

          {/* Color dot */}
          {nodeColor && (
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: nodeColor,
                flexShrink: 0,
              }}
            />
          )}

          {editingPath === node.id ? (
            <TextField
              size="small"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleRename(node.id, editValue)
                }
                if (e.key === 'Escape') {
                  setEditingPath(null)
                }
              }}
              onBlur={() => handleRename(node.id, editValue)}
              autoFocus
              sx={{ flex: 1 }}
            />
          ) : (
            <>
              <Typography
                draggable={node.isTag}
                onClick={(e) => {
                  e.stopPropagation()
                  focusTree()
                  if (mode === 'manage') {
                    setInspectedPath(node.id)
                  }
                }}
                onDragStart={(e) => {
                  e.stopPropagation()
                  const idsToDrag = selectedPaths.includes(node.id) ? selectedPaths : [node.id]
                  setDraggedTagIds(idsToDrag)
                  setDropTarget(null)
                  e.dataTransfer.effectAllowed = 'move'
                  const row = (e.currentTarget as HTMLElement).closest(
                    `[data-tag-row="${node.id}"]`,
                  ) as HTMLElement | null
                  if (row) {
                    const rect = row.getBoundingClientRect()
                    e.dataTransfer.setDragImage(row, Math.min(rect.width / 2, 140), rect.height / 2)
                  }
                }}
                onDragEnd={(e) => {
                  e.stopPropagation()
                  setDraggedTagIds([])
                  setDropTarget(null)
                }}
                sx={{
                  flex: 1,
                  fontSize: hasChildren && !node.isTag ? '0.88rem' : '0.82rem',
                  fontWeight: hasChildren ? 600 : 400,
                  color: !node.isTag ? 'var(--text-secondary)' : 'var(--text-primary)',
                  opacity: isHidden ? 0.55 : 1,
                  cursor: mode === 'manage' ? 'pointer' : node.isTag ? 'grab' : 'default',
                  display: 'flex',
                  alignItems: 'center',
                  '&:active': { cursor: node.isTag ? 'grabbing' : 'default' },
                }}
              >
                {!hasChildren && (
                  <Box
                    component="span"
                    sx={{ color: 'text.disabled', marginInlineEnd: 0.5, fontSize: '0.9em', userSelect: 'none' }}
                  >
                    #
                  </Box>
                )}
                {node.name}
              </Typography>
              {count > 0 && (
                <Typography
                  component="span"
                  sx={{
                    fontSize: '0.64rem',
                    color: 'var(--text-tertiary)',
                    minWidth: 18,
                    textAlign: 'center',
                  }}
                >
                  {count}
                </Typography>
              )}
              {isHidden && (
                <Typography
                  component="span"
                  sx={{ fontSize: '0.6rem', color: 'warning.main', minWidth: 34, textAlign: 'center' }}
                >
                  hidden
                </Typography>
              )}
              <Box className="node-actions" sx={{ display: 'flex', ...iconBtnSx }} onClick={(e) => e.stopPropagation()}>
                <IconButton
                  size="small"
                  sx={{ p: 0.25 }}
                  onClick={() => {
                    setEditingPath(node.id)
                    setEditValue(node.name)
                  }}
                  title="Rename"
                >
                  <Edit sx={{ fontSize: 16 }} />
                </IconButton>
                <IconButton
                  size="small"
                  sx={{ p: 0.25 }}
                  onClick={() => {
                    setAddingUnder(node.id)
                    setNewTagValue('')
                  }}
                  title="Add child tag"
                >
                  <Add sx={{ fontSize: 16 }} />
                </IconButton>
                <IconButton
                  size="small"
                  sx={{ p: 0.25 }}
                  onClick={() => handleToggleHidden(node.id)}
                  title={isHidden ? 'Unhide' : 'Hide'}
                >
                  {isHidden ? <Visibility sx={{ fontSize: 16 }} /> : <VisibilityOff sx={{ fontSize: 16 }} />}
                </IconButton>
                {!isProtectedTag(node.id) && (
                  <IconButton size="small" sx={{ p: 0.25 }} onClick={() => handleDelete(node.id)} title="Delete">
                    <Delete sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
              </Box>
            </>
          )}
        </Box>

        {isExpanded && node.children.map((child) => renderNode(child, depth + 1))}

        {addingUnder === node.id && renderAddRow(node.id, depth + 1)}
      </Box>
    )
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      dir={direction}
      sx={{
        '& .MuiDialog-container': {
          alignItems: 'flex-start',
        },
        '& .MuiDialog-paper': {
          mt: 2,
          mb: 2,
          height: 'min(820px, calc(100vh - 32px))',
          display: 'flex',
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {isSelectMode ? 'Select Tags' : 'Manage Tags'}
        <IconButton size="small" onClick={onClose}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, display: 'flex', minHeight: 0, flex: 1 }}>
        {loading ? (
          <Typography sx={{ color: 'var(--text-secondary)', py: 4, textAlign: 'center', width: '100%' }}>
            Loading...
          </Typography>
        ) : (
          <>
            {/* ---- Tree pane ---- */}
            <Box
              sx={{
                width: '50%',
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                borderRight: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box
                sx={{
                  p: 1,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  zIndex: 30,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <TextField
                    size="small"
                    placeholder="Search tags"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      flex: 1,
                      minWidth: 180,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 999,
                        bgcolor: 'background.default',
                      },
                      '& .MuiInputBase-input': {
                        fontSize: '0.92rem',
                        py: 1.05,
                      },
                      '& .MuiInputAdornment-root .MuiSvgIcon-root': {
                        fontSize: 18,
                      },
                    }}
                  />
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.9,
                      px: 1.2,
                      py: 0.45,
                      borderRadius: 999,
                      border: '1px solid',
                      borderColor: showHidden ? 'primary.main' : 'divider',
                      bgcolor: showHidden ? 'action.selected' : 'background.default',
                      flexShrink: 0,
                    }}
                  >
                    <VisibilityOff sx={{ fontSize: 15, color: showHidden ? 'primary.main' : 'text.secondary' }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Typography
                        sx={{ fontSize: '0.76rem', fontWeight: 500, color: 'text.primary', whiteSpace: 'nowrap' }}
                      >
                        Show hidden
                      </Typography>
                      <Box
                        sx={{
                          minWidth: 20,
                          px: 0.6,
                          py: 0.1,
                          borderRadius: 999,
                          bgcolor: showHidden ? 'primary.main' : 'action.hover',
                          color: showHidden ? 'primary.contrastText' : 'text.secondary',
                          fontSize: '0.68rem',
                          lineHeight: 1.4,
                          textAlign: 'center',
                        }}
                      >
                        {hiddenIds.size}
                      </Box>
                    </Box>
                    <Switch
                      size="small"
                      checked={showHidden}
                      onChange={(e) => setShowHidden(e.target.checked)}
                      inputProps={{ 'aria-label': 'Show hidden tags' }}
                    />
                  </Box>
                </Box>
              </Box>

              <Box
                ref={scrollContainerRef}
                tabIndex={0}
                onKeyDown={handleTreeKeyDown}
                sx={{
                  overflowY: 'auto',
                  px: 1,
                  pb: 1,
                  pt: 0,
                  flex: 1,
                  outline: 'none',
                }}
              >
                {tree.length > 0 ? (
                  tree.map((node) => renderNode(node, 0))
                ) : (
                  <Typography sx={{ px: 1, py: 2, color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                    No tags match the current filters.
                  </Typography>
                )}

                {addingAtRoot ? (
                  renderAddRow(null, 0)
                ) : (
                  <Button
                    size="small"
                    startIcon={<Add />}
                    onClick={() => {
                      setAddingAtRoot(true)
                      setNewTagValue('')
                    }}
                    sx={{ mt: 1, mx: 1, textTransform: 'none' }}
                  >
                    Add Tag
                  </Button>
                )}
              </Box>
            </Box>

            {/* ---- Detail pane ---- */}
            <Box sx={{ flex: 1, p: 2, overflowY: 'auto' }} dir="ltr">
              {isMultipleSelectMode && selectedTagLabels.length > 0 && (
                <Box sx={{ mb: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                    Selected
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {selectedTagLabels.map((tag) => (
                      <Chip
                        key={tag.id}
                        label={tag.name}
                        size="small"
                        onDelete={() => setSelectedPaths((prev) => prev.filter((id) => id !== tag.id))}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {selectedPaths.length > 1 && mode === 'manage' && inspectedPath == null ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {selectedPaths.length} tags selected
                  </Typography>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<Delete />}
                      onClick={() => {
                        const protectedIds = selectedPaths.filter(isProtectedTag)
                        const pathsToDelete = selectedPaths.filter((id) => !isProtectedTag(id))
                        if (protectedIds.length > 0) {
                          const names = protectedIds.map(getProtectedTagName).join(', ')
                          toast.error(`Protected tags cannot be removed: ${names}`)
                        }
                        if (pathsToDelete.length === 0) {
                          return
                        }
                        const descendantCount = pathsToDelete.reduce((acc, id) => {
                          if (!tags[id]) {
                            return acc
                          }
                          return acc + Math.max(0, collectDescendantIds(tags, id).size - 1)
                        }, 0)

                        if (
                          window.confirm(
                            `Delete ${pathsToDelete.length} tags${descendantCount > 0 ? ` and ${descendantCount} descendants` : ''}?`,
                          )
                        ) {
                          const updated = { ...tags }
                          pathsToDelete.forEach((id) => {
                            if (!updated[id]) {
                              return
                            }
                            const idsToDelete = collectDescendantIds(updated, id)
                            for (const k of idsToDelete) {
                              delete updated[k]
                            }
                          })
                          setTags(updated)
                          setSelectedPaths([])
                          setLastSelectedPath(null)
                        }
                      }}
                    >
                      Delete Selected
                    </Button>

                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<VisibilityOff />}
                      onClick={() => {
                        setTags((prev) => {
                          const next = { ...prev }
                          selectedPaths.forEach((id) => {
                            if (next[id]) {
                              next[id] = { ...next[id], hidden: true }
                            }
                          })
                          return next
                        })
                        setSelectedPaths([])
                        setLastSelectedPath(null)
                      }}
                    >
                      Hide Selected
                    </Button>

                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Visibility />}
                      onClick={() => {
                        setTags((prev) => {
                          const next = { ...prev }
                          selectedPaths.forEach((id) => {
                            if (next[id]) {
                              next[id] = { ...next[id], hidden: false }
                            }
                          })
                          return next
                        })
                        setSelectedPaths([])
                        setLastSelectedPath(null)
                      }}
                    >
                      Unhide Selected
                    </Button>
                  </Box>

                  <Box>
                    <Typography variant="caption" sx={{ color: 'var(--text-secondary)', mb: 0.75, display: 'block' }}>
                      Set Color for Selected
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                      {TAG_COLORS.map(({ value, label }) => (
                        <Box
                          key={value}
                          title={label}
                          onClick={() => {
                            setTags((prev) => {
                              const next = { ...prev }
                              selectedPaths.forEach((id) => {
                                if (next[id]) {
                                  next[id] = { ...next[id], color: value }
                                }
                              })
                              return next
                            })
                            setSelectedPaths([])
                            setLastSelectedPath(null)
                          }}
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            bgcolor: value,
                            cursor: 'pointer',
                            border: '2px solid transparent',
                            boxShadow: 'inset 0 0 0 1px rgba(17, 24, 39, 0.16)',
                            transition: 'opacity 0.15s, transform 0.15s',
                            '&:hover': { opacity: 0.9, transform: 'scale(1.05)' },
                          }}
                        />
                      ))}
                      <Button
                        size="small"
                        onClick={() => {
                          setTags((prev) => {
                            const next = { ...prev }
                            selectedPaths.forEach((id) => {
                              if (next[id]) {
                                next[id] = { ...next[id], color: undefined }
                              }
                            })
                            return next
                          })
                          setSelectedPaths([])
                          setLastSelectedPath(null)
                        }}
                      >
                        Clear Color
                      </Button>
                    </Box>
                  </Box>
                </Box>
              ) : detailPath && detailMeta != null ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {/* Display path */}
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
                    {getTagDisplayName(tags, detailPath)}
                  </Typography>

                  {/* Usage count */}
                  {(() => {
                    const direct = usageCounts[detailPath] || 0
                    const total = getNodeCount(detailPath, usageCounts, tags)
                    const indirect = total - direct
                    if (total === 0) {
                      return (
                        <Typography variant="body2" sx={{ color: 'var(--text-tertiary)' }}>
                          No annotations
                        </Typography>
                      )
                    }
                    return (
                      <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                        {direct} direct annotation{direct !== 1 ? 's' : ''}
                        {indirect > 0 && ` + ${indirect} from children`}
                      </Typography>
                    )
                  })()}

                  {/* Color */}
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" sx={{ color: 'var(--text-secondary)', mb: 0.75, display: 'block' }}>
                      Color
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {TAG_COLORS.map(({ value, label }) => (
                        <Box
                          key={value}
                          title={label}
                          onClick={() =>
                            updateMeta(detailPath, { color: detailMeta.color === value ? undefined : value })
                          }
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            bgcolor: value,
                            cursor: 'pointer',
                            border: detailMeta.color === value ? '3px solid' : '2px solid transparent',
                            borderColor: detailMeta.color === value ? 'text.primary' : 'transparent',
                            boxShadow: 'inset 0 0 0 1px rgba(17, 24, 39, 0.16)',
                            transition: 'border-color 0.15s, transform 0.15s',
                            '&:hover': { opacity: 0.9, transform: 'scale(1.05)' },
                          }}
                        />
                      ))}
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
                    <TextField
                      size="small"
                      type="number"
                      label="Order"
                      value={detailMeta.order || 0}
                      onChange={(e) =>
                        updateMeta(detailPath, {
                          order: Number.isFinite(Number(e.target.value)) ? Number(e.target.value) : 0,
                        })
                      }
                      sx={{ width: 110 }}
                    />
                    <Button
                      size="small"
                      color={detailMeta.hidden ? 'success' : 'warning'}
                      startIcon={detailMeta.hidden ? <Visibility /> : <VisibilityOff />}
                      onClick={() => handleToggleHidden(detailPath)}
                    >
                      {detailMeta.hidden ? 'Unhide' : 'Hide'}
                    </Button>
                  </Box>

                  {/* Description */}
                  <Box>
                    <Typography variant="caption" sx={{ color: 'var(--text-secondary)', mb: 0.75, display: 'block' }}>
                      Description
                    </Typography>
                    <TextField
                      size="small"
                      multiline
                      minRows={3}
                      maxRows={8}
                      fullWidth
                      placeholder="Add notes about this tag..."
                      value={detailMeta.description || ''}
                      onChange={(e) => updateMeta(detailPath, { description: e.target.value || undefined })}
                      inputProps={{ dir: direction }}
                      sx={{
                        '& .MuiInputBase-inputMultiline': {
                          textAlign: direction === 'rtl' ? 'right' : 'left',
                        },
                      }}
                    />
                  </Box>

                  {/* Preview pill */}
                  <Box>
                    <Typography variant="caption" sx={{ color: 'var(--text-secondary)', mb: 0.75, display: 'block' }}>
                      Preview
                    </Typography>
                    <TagPillPreview tagId={detailPath} tags={tags} color={detailMeta.color} direction={direction} />
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--text-secondary)',
                  }}
                >
                  <Typography variant="body2">
                    {isMultipleSelectMode
                      ? 'Select one or more tags to assign'
                      : isSelectMode
                        ? 'Select a tag to use for this selection'
                        : 'Select a tag to edit its properties'}
                  </Typography>
                </Box>
              )}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        {!isSelectMode && (
          <Button onClick={() => handleSave(false)} disabled={saving || loading}>
            Apply
          </Button>
        )}
        <Button
          onClick={() => handleSave(true)}
          variant="contained"
          disabled={
            saving || loading || (isMultipleSelectMode ? selectedPaths.length === 0 : isSelectMode && !selectedPath)
          }
        >
          {saving ? 'Saving...' : isSelectMode ? 'Save Selection' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

/** Small preview of how the tag pill will look in the editor */
function TagPillPreview({
  tagId,
  tags,
  color,
  direction,
}: {
  tagId: string
  tags: TagMap
  color?: string
  direction: 'ltr' | 'rtl'
}) {
  const label = getTagDisplayName(tags, tagId)
  const c = color || '#1976d2'

  return (
    <Box
      component="span"
      dir={direction}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        height: '22px',
        px: 1,
        fontSize: '11px',
        fontWeight: 600,
        color: c,
        backgroundColor: `${c}22`,
        border: `1px solid ${c}66`,
        borderRadius: '11px',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Box>
  )
}

export default TagManagerDialog
